<template lang="pug">
#resident-detail(v-cloak)
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            b-spinner.spinner.mr-2(v-if="resident == null")
            span(v-else-if="resident.name") {{ resident.name }}
            span(v-else).warning-message 未入力
            span 様の詳細
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.mr-2(type="button" @click="onResidentTransfer") 他施設へ移動／コピー
          button.btn.notification-btn(type="button" @click="onBack") 戻る
    .box-body
      b-tabs(v-model="tabIndex")
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span 利用者情報
          ResidentDetailTab(ref="residentDetailTab" v-model="resident")
        b-tab.tab(v-if="_useWaiterService && _existWaiter")
          template(v-slot:title)
            .tab-title
              span 申込内容・登録情報
          WaiterDetailTab(ref="waiterDetailTab" v-model="resident" :waiterViewableFacilitiesMap="waiterViewableFacilitiesMap")
        b-tab.tab(v-if="_useWaiterService && _isJudged && _isOverWaiterBasicPlan")
          template(v-slot:title)
            .tab-title
              span 入所評価判定情報
          WaiterJudgedTab(ref="waiterJudgedTab" v-model="resident")
        b-tab.tab(v-if="_useWaiterService && _existWaiter && _isOverWaiterBasicPlan")
          template(v-slot:title)
            .tab-title
              span 経過記録
          WaiterProgressHistoriesTab(ref="waiterProgressHistoriesTab" v-model="resident" :waiterViewableFacilitiesMap="waiterViewableFacilitiesMap")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")

  TransferResidentInputModal(v-model="showTransferResidentInputModal" :errorMessage="transferResidentInputModalErrorMessage" 
    @ok="confirmTransferResident")
  OkCancelModal(v-model="showTransferResidentConfirmModal" :title="transferResidentConfirmModalTitle" @ok="transferResident")
  OkModal(v-model="showTransferResidentSuccessModal" :title="transferResidentSuccessModalTitle" @ok="successTransfer")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore } from "@/store/store";
import LoginUser from "@/store/LoginUser";
import { PcUrl } from "@/commons/constants/PcUrl";

import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import ResidentsService, { ResidentGetModel, ResidentTransferPostModel } from "@/commons/services/Residents";
import WaiterProgressHistoriesService from "@/commons/services/WaiterProgressHistories";
import { WaiterServicePlanType } from "@/commons/services/WaiterServicePlans";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ResidentDetailTab from "@/pc/containers/residents/ResidentDetailTab.vue";
import WaiterDetailTab from "@/pc/containers/residents/WaiterDetailTab.vue";
import WaiterJudgedTab from "@/pc/containers/residents/WaiterJudgedTab.vue";
import WaiterProgressHistoriesTab from "@/pc/containers/residents/WaiterProgressHistoriesTab.vue";
import TransferResidentInputModal from "@/pc/containers/residents/TransferResidentInputModal.vue";
import SystemError from "@/commons/models/SystemError";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### 待機者一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkModal,
    OkCancelModal,
    ResidentDetailTab,
    WaiterDetailTab,
    WaiterJudgedTab,
    WaiterProgressHistoriesTab,
    TransferResidentInputModal
  }
})
export default class WaiterDetail extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 待機者経過履歴サービス */
  @Inject("WaiterProgressHistoriesService") waiterProgressHistoriesService!: WaiterProgressHistoriesService;

  @Ref() residentDetailTab!: ResidentDetailTab;
  @Ref() waiterDetailTab!: WaiterDetailTab;
  @Ref() waiterJudgedTab!: WaiterJudgedTab;
  @Ref() waiterProgressHistoriesTab!: WaiterProgressHistoriesTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 完了モーダル表示可否 */
  showSuccessModal: boolean = false;
  /** 完了モーダルタイトル */
  successModalTitle: string = "";

  /** 利用者の移動／コピーモーダル表示可否 */
  showTransferResidentInputModal: boolean = false;
  /** 利用者の移動／コピーモーダルで表示するエラーメッセージ */
  transferResidentInputModalErrorMessage: string = "";
  /** 利用者の移動／コピー確認モーダル表示可否 */
  showTransferResidentConfirmModal: boolean = false;
  /** 利用者の移動／コピー確認モーダルタイトル */
  transferResidentConfirmModalTitle: string = "";
  /** 利用者の移動／コピー完了モーダル表示可否 */
  showTransferResidentSuccessModal: boolean = false;
  /** 利用者の移動／コピー確認モーダルタイトル */
  transferResidentSuccessModalTitle: string = "";

  /** 利用者 */
  resident: ResidentGetModel | null = null;
  /** 閲覧可能施設のMAP。key：施設ID、value：施設 */
  waiterViewableFacilitiesMap: Map<number, FacilityGetModel> | null = null;

  /** 移動／コピーモーダルの入力情報 */
  residentTransferPostModel: ResidentTransferPostModel | null = null;

  /** 現在表示しているタブ番号 */
  tabIndex: number = 0;

  /** ### 待機者サービスを使用しているか否か */
  get _useWaiterService(): boolean {
    return userStore.facility.useWaiterService && userStore.loginUser.permissions.functions.useWaiterService;
  }

  /** ### プラン(待機者)がベーシック以上かを返す */
  get _isOverWaiterBasicPlan(): boolean {
    const planType: WaiterServicePlanType = userStore.facility.waiterServicePlanType;
    if (WaiterServicePlanType.BASIC <= planType) {
      return true;
    } else {
      return false;
    }
  }

  /** ### 待機者情報が存在するか */
  get _existWaiter(): boolean {
    if (!this.resident || !this.resident.waiter) {
      return false;
    } else {
      return true;
    }
  }

  /** ### 待機者の判定されているか */
  get _isJudged(): boolean {
    if (!this.resident || !this.resident.waiter || !this.resident.waiter.admissionJudgement) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  public mounted(): void {
    this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    const residentId: number = Number(this.$route.params.id);
    const resident: ResidentGetModel = await this.getResident(residentId);
    this.resident = resident;

    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities(userStore);
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = this.createWaiterViewableFacilitiesMap(waiterViewableFacilities);
    this.waiterViewableFacilitiesMap = waiterViewableFacilitiesMap;

    return;
  }

  /**
   * ### 利用者を取得する
   */
  public async getResident(residentId: number): Promise<ResidentGetModel> {
    const result: ResidentGetModel = await this.residentsService.get(userStore, residentId);
    return result;
  }

  /**
   * ### 閲覧可能施設を取得する
   */
  public async getWaiterViewableFacilities(userStore: LoginUser): Promise<Array<FacilityGetModel>> {
    const waiterViewableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
      waiterViewableFacilities.push(response);
    }
    return waiterViewableFacilities;
  }

  /**
   * ### 閲覧可能施設をMAP化する
   *
   * - key：施設ID、value：施設情報
   * - 自施設もMAPに含める
   */
  public createWaiterViewableFacilitiesMap(waiterViewableFacilities: Array<FacilityGetModel>): Map<number, FacilityGetModel> {
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
    waiterViewableFacilitiesMap.set(userStore.facility.id, userStore.facility);
    for (const facility of waiterViewableFacilities) {
      waiterViewableFacilitiesMap.set(facility.id, facility);
    }
    return waiterViewableFacilitiesMap;
  }

  /**
   * ### 利用者の移動／コピーモーダル（入力モーダル）を表示する
   */
  public onResidentTransfer(): void {
    this.showTransferResidentInputModal = true;
    return;
  }

  /**
   * ### 利用者の移動／コピーの確認モーダルを表示する
   */
  public confirmTransferResident(residentTransferPostModel: ResidentTransferPostModel): void {
    this.residentTransferPostModel = residentTransferPostModel;
    const transferType: string = this.residentTransferPostModel!.needDelete ? "移動" : "コピー";
    this.transferResidentConfirmModalTitle = `利用者を${transferType}しますか？`;
    this.showTransferResidentConfirmModal = true;
    return;
  }

  /**
   * ### 利用者の移動／コピー
   *
   * - 利用者の移動／コピーモーダルで入力した内容でAPIリクエストする
   * - エラー無しで完了した場合は利用者の移動／コピーモーダルを非表示にする
   * - エラーの場合は利用者の移動／コピーモーダルを表示したまま、エラーメッセージを表示する
   */
  public async transferResident(): Promise<void> {
    const transferType: string = this.residentTransferPostModel!.needDelete ? "移動" : "コピー";

    try {
      this.loadingMessage = `利用者を${transferType}してします...`;
      this.isBusy = true;

      await this.residentsService.postTransfer(userStore, this.resident!.id, this.residentTransferPostModel as ResidentTransferPostModel);

      this.showTransferResidentInputModal = false;
      this.transferResidentSuccessModalTitle = `${this.resident!.name}の${transferType}に成功しました`;
      this.showTransferResidentSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // 422の場合は上限エラーなのでエラーメッセージを表示する
      if (systemError.statusCode == 422) {
        this.transferResidentInputModalErrorMessage = `${transferType}先施設のご契約プランの利用者登録数上限に達しているため${transferType}できません`;
        return;
      }
      // 409の場合は移動／コピー先施設に利用者が存在するのでエラーメッセージを表示する
      else if (systemError.statusCode == 409) {
        this.transferResidentInputModalErrorMessage = `${transferType}先施設に既に同じ利用者が登録されているため${transferType}できません`;
        return;
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }
    return;
  }

  /**
   * ### 移動／コピー完了モーダルのOKボタン押下イベント
   *
   * - 移動の場合は一覧画面へ遷移する
   * - コピーの場合はなにもしない
   */
  public successTransfer(): void {
    if (this.residentTransferPostModel!.needDelete) {
      this.onBack();
    } else {
      return;
    }
  }

  /**
   * ### 利用者一覧画面へ遷移する
   */
  public onBack(): void {
    this.$router.push(PcUrl.ResidentList);
    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      if (this._useWaiterService && this._existWaiter && this._isOverWaiterBasicPlan) {
        this.waiterProgressHistoriesTab.setFilterSetting();
      }
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#resident-detail {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
    .spinner {
      width: 22px;
      height: 22px;
    }
  }
}
</style>
